import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Avatar } from "@/components/Avatar";
import type { Notification } from "@/messaging/types";
import { CloseIcon, ExternalLinkIcon, MegaphoneIcon } from "@/messaging/chat/icons";
import { fullTime, safeLink, timeAgo, unusedQuickReactions } from "./shared";

/** Full announcement view + reactions. Portaled above the bell. */
export function NotificationDetail({
  n,
  onClose,
  onReact,
}: {
  n: Notification;
  onClose: () => void;
  onReact: (emoji: string) => void;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const sender = n.payload as {
    sender_name?: string;
    sender_email?: string;
    sender_avatar_url?: string | null;
  };
  const href = safeLink(n.link_url);
  const internal = !!href?.startsWith("/");
  // Only offer faces nobody has used yet — the counted pills above already
  // carry the rest, and showing both made 👍 appear twice.
  const quick = unusedQuickReactions(n);

  return createPortal(
    <div
      className="fixed inset-0 z-[1350] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-md shadow-2xl animate-[fadeIn_.15s_ease-out]"
      >
        <div className="card-header justify-between">
          <div className="flex items-center gap-2 text-accent-300">
            <MegaphoneIcon size={16} />
            <span className="card-title !text-accent-300">Announcement</span>
          </div>
          <button type="button" onClick={onClose} className="icon-btn h-8 w-8" title="Close">
            <CloseIcon size={16} />
          </button>
        </div>
        <div className="px-5 py-4">
          <h3 className="text-base font-semibold text-slate-100">{n.title}</h3>
          {n.body && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{n.body}</p>}
          {href &&
            (internal ? (
              // Router navigation, not a raw href — an internal link
              // otherwise hard-reloads the whole SPA.
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate(href);
                }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-accent-300 transition hover:text-accent-200"
              >
                Open link <ExternalLinkIcon size={14} />
              </button>
            ) : (
              <a
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-accent-300 transition hover:text-accent-200"
              >
                Open link <ExternalLinkIcon size={14} />
              </a>
            ))}
          <div className="mt-4 flex items-center gap-2 border-t border-white/5 pt-3 text-[11px] text-slate-500">
            {sender.sender_name && (
              <>
                <Avatar
                  url={sender.sender_avatar_url}
                  name={sender.sender_name}
                  email={sender.sender_email}
                  size="xs"
                  className="!h-5 !w-5"
                />
                <span>{sender.sender_name}</span>
                <span>·</span>
              </>
            )}
            <span title={fullTime(n.created_at)}>{timeAgo(n.created_at)}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {n.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                onClick={() => onReact(r.emoji)}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] transition ${
                  r.me
                    ? "border-accent-500/50 bg-accent-500/15 text-accent-200"
                    : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/20"
                }`}
                title={r.users.map((u) => u.full_name || u.email).join(", ")}
              >
                <span>{r.emoji}</span>
                <span className="tabular-nums">{r.count}</span>
              </button>
            ))}
            {n.reactions.length > 0 && quick.length > 0 && (
              <span className="mx-0.5 h-4 w-px bg-white/10" />
            )}
            {quick.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => onReact(e)}
                className="h-7 w-7 rounded-full text-base leading-none text-slate-400 opacity-70 transition hover:bg-white/10 hover:opacity-100"
                title={`React ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
